import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

//Angular Material
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MtxDrawerModule } from '@ng-matero/extensions/drawer';
import { MtxButtonModule } from '@ng-matero/extensions/button';
import { MtxLoaderModule } from '@ng-matero/extensions/loader';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SortableTableComponent } from '@app/pages/authenticated/components/sortable-table/sortable-table.component';
import { TableColumnDirective } from '@app/pages/authenticated/components/sortable-table/table-column.directive';
import { DataTableComponent } from '@app/pages/authenticated/components/data-table/data-table.component';
import { CustomFormatHelper } from './utilities/custom-format.helper';
import { FilterDrawerComponent } from '@app/pages/authenticated/components/filter-drawer/filter-drawer.component';
import { SendEmailDrawerComponent } from '@app/pages/authenticated/components/send-email-drawer/send-email-drawer.component';
import { HistoryDialogComponent } from '@app/pages/authenticated/components/history-dialog/history-dialog.component';
import { SubscriptionDetailComponent } from '@app/pages/authenticated/components/subscription-detail/subscription-detail.component';
import { SignaturePadComponent } from '@app/pages/authenticated/components/signature-pad/signature-pad.component';
import { RequestPaymentComponent } from '@app/pages/authenticated/components/request-payment/request-payment.component';
import { BypassPaymentComponent } from '@app/pages/authenticated/components/bypass-payment/bypass-payment.component';
import { CustomerDetailsComponent } from '@app/pages/authenticated/components/customer-details/customer-details.component';
import { ReturnRemoveDrawerComponent } from '@app/pages/authenticated/components/return-remove-drawer/return-remove-drawer.component';
import { QRCodeComponent  } from 'angularx-qrcode';
import { AccountNumberDialogComponent } from '@app/pages/authenticated/components/account-number-dialog/account-number-dialog.component';
import { NoDataComponent } from '@app/pages/authenticated/components/no-data/no-data.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { SendReceiptPopupComponent } from '@app/pages/authenticated/components/send-receipt-popup/send-receipt-popup.component';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AnalyticsChartComponent } from '../pages/authenticated/components/analytics-chart/analytics-chart.component';
import { PriceBreakdownComponent } from '@app/pages/unauthenticated/pages/customer-shipping-tool/components/price-breakdown/price-breakdown.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PackageImagePopupComponent } from '@app/pages/authenticated/components/package-image-popup/package-image-popup.component';
import { TimelineHistoryDialogComponent } from '@app/pages/authenticated/components/timeline-history-dialog/timeline-history-dialog.component';
import { PrintConfirmationDialogComponent } from '@app/pages/authenticated/components/print-confirmation-dialog/print-confirmation-dialog.component';
import { ViewPdfDialogComponent } from '@app/pages/authenticated/components/view-pdf-dialog/view-pdf-dialog.component';
import { PhoneFormatDirective } from './directives/phone-format.directive';
import { DateRangePickerComponent } from '@app/pages/authenticated/components/date-range-picker/date-range-picker.component';
import { MaterialMonthCalendarPickerComponent } from '@app/pages/authenticated/components/material-month-calendar-picker/material-month-calendar-picker.component';
import { KpiTileComponent } from '@app/pages/authenticated/components/kpi-tile/kpi-tile.component';
import { VolumeTableComponent } from '@app/pages/authenticated/components/volume-table/volume-table.component';
import { ComparisonPeriodLabelComponent } from '@app/pages/authenticated/components/comparison-period-label/comparison-period-label.component';
import { ReportDataWarningComponent } from '@app/pages/authenticated/components/report-data-warning/report-data-warning.component';
import { VolumeKpiGridComponent } from '@app/pages/authenticated/components/volume-kpi-grid/volume-kpi-grid.component';
import { VolumeTrendCardComponent } from '@app/pages/authenticated/components/volume-trend-card/volume-trend-card.component';
import { ComparisonBadgeComponent } from '@app/pages/authenticated/components/comparison-badge/comparison-badge.component';
import { TypeformSurveyReportComponent } from '@app/pages/authenticated/components/typeform-survey-report/typeform-survey-report.component';
import { CommissionEstimateReportComponent } from '@app/pages/authenticated/components/commission-estimate-report/commission-estimate-report.component';
import { RevenueReportComponent } from '@app/pages/authenticated/components/revenue-report/revenue-report.component';
import { FinancialOverviewReportComponent } from '@app/pages/authenticated/components/financial-overview-report/financial-overview-report.component';
import { FinancialOverviewTableComponent } from '@app/pages/authenticated/components/financial-overview-table/financial-overview-table.component';
import { CommissionEstimateTableComponent } from '@app/pages/authenticated/components/commission-estimate-table/commission-estimate-table.component';

export function playerFactory() {
  return player;
}

@NgModule({
  declarations: [
    DataTableComponent,
    FilterDrawerComponent,
    CustomFormatHelper,
    SendEmailDrawerComponent,
    HistoryDialogComponent,
    SubscriptionDetailComponent,
    SignaturePadComponent,
    RequestPaymentComponent,
    BypassPaymentComponent,
    CustomerDetailsComponent,
    ReturnRemoveDrawerComponent,
    AccountNumberDialogComponent,
    NoDataComponent,
    PriceBreakdownComponent,
    SendReceiptPopupComponent,
    AnalyticsChartComponent,
    PackageImagePopupComponent,
    TimelineHistoryDialogComponent,
    PrintConfirmationDialogComponent,
    ViewPdfDialogComponent,
    PhoneFormatDirective,
    DateRangePickerComponent,
    MaterialMonthCalendarPickerComponent,
    KpiTileComponent,
    ComparisonPeriodLabelComponent,
    ReportDataWarningComponent,
    VolumeKpiGridComponent,
    VolumeTrendCardComponent,
    VolumeTableComponent,
    SortableTableComponent,
    TableColumnDirective,
    ComparisonBadgeComponent,
    TypeformSurveyReportComponent,
    CommissionEstimateReportComponent,
    CommissionEstimateTableComponent,
    RevenueReportComponent,
    FinancialOverviewReportComponent,
    FinancialOverviewTableComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MtxDrawerModule,
    MtxButtonModule,
    MtxLoaderModule,
    MtxTooltipModule,
    MatPaginatorModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatExpansionModule,
    MatDividerModule,
    MatInputModule,
    MatListModule,
    MatDatepickerModule,
    MatButtonModule,
    MatBadgeModule,
    MatRadioModule,
    QRCodeComponent,
    MatTabsModule,
    MatTooltip,
    LottieComponent,
    MatTimepickerModule,
    MatDialogModule,
    MatMenuModule,
    MatIconModule,
    NgxChartsModule,
    NgxMatSelectSearchModule,
    MatDatepickerModule,
    TextFieldModule
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatCardModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatSidenavModule,
    MatSelectModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatNativeDateModule,
    MatRippleModule,
    MatMenuModule,
    MatListModule,
    MatInputModule,
    MatIconModule,
    MatGridListModule,
    MatExpansionModule,
    MatDividerModule,
    MatDialogModule,
    MatDatepickerModule,
    MatStepperModule,
    MatChipsModule,
    MatCheckboxModule,
    MatCardModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatTableModule,
    TranslateModule,
    MatSnackBarModule,
    DataTableComponent,
    FilterDrawerComponent,
    CustomFormatHelper,
    SendEmailDrawerComponent,
    HistoryDialogComponent,
    SubscriptionDetailComponent,
    SignaturePadComponent,
    RequestPaymentComponent,
    BypassPaymentComponent,
    CustomerDetailsComponent,
    ReturnRemoveDrawerComponent,
    QRCodeComponent,
    AccountNumberDialogComponent,
    NoDataComponent,
    MatTabsModule,
    MatTooltip,
    LottieComponent,
    PriceBreakdownComponent,
    SendReceiptPopupComponent,
    MatTimepickerModule,
    AnalyticsChartComponent,
    NgxChartsModule,
    PackageImagePopupComponent,
    TimelineHistoryDialogComponent,
    PrintConfirmationDialogComponent,
    ViewPdfDialogComponent,
    TextFieldModule,
    PhoneFormatDirective,
    DateRangePickerComponent,
    MaterialMonthCalendarPickerComponent,
    KpiTileComponent,
    ComparisonPeriodLabelComponent,
    ReportDataWarningComponent,
    VolumeKpiGridComponent,
    VolumeTrendCardComponent,
    VolumeTableComponent,
    SortableTableComponent,
    TableColumnDirective,
    TypeformSurveyReportComponent,
    CommissionEstimateReportComponent,
    CommissionEstimateTableComponent,
    RevenueReportComponent,
    FinancialOverviewReportComponent,
    FinancialOverviewTableComponent,
    MatSortModule,
  ],
  providers: [
    provideLottieOptions({
      player: playerFactory
    })
  ],
})
export class SharedModule {}
